import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

interface RefreshContextType {
  registerRefresh: (path: string, refetchFn: () => Promise<void>) => void;
  unregisterRefresh: (path: string) => void;
  refresh: (path: string) => Promise<void>;
  isRefreshing: boolean;
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

const REFRESH_COOLDOWN = 2000; // 2 seconds cooldown to prevent spam

export const RefreshProvider = ({ children }: { children: ReactNode }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refetchFunctions = useRef<Map<string, () => Promise<void>>>(new Map());
  const lastRefreshTime = useRef<Map<string, number>>(new Map());

  const registerRefresh = useCallback((path: string, refetchFn: () => Promise<void>) => {
    refetchFunctions.current.set(path, refetchFn);
  }, []);

  const unregisterRefresh = useCallback((path: string) => {
    refetchFunctions.current.delete(path);
    lastRefreshTime.current.delete(path);
  }, []);

  const refresh = useCallback(async (path: string) => {
    const now = Date.now();
    const lastRefresh = lastRefreshTime.current.get(path) || 0;
    const timeSinceLastRefresh = now - lastRefresh;

    // Prevent spam: enforce cooldown period
    if (timeSinceLastRefresh < REFRESH_COOLDOWN) {
      return;
    }

    const refetchFn = refetchFunctions.current.get(path);
    if (!refetchFn) return;

    setIsRefreshing(true);
    lastRefreshTime.current.set(path, now);

    try {
      await refetchFn();
    } finally {
      // Keep spinner visible for minimum 500ms for better UX
      setTimeout(() => setIsRefreshing(false), 500);
    }
  }, []);

  return (
    <RefreshContext.Provider value={{ registerRefresh, unregisterRefresh, refresh, isRefreshing }}>
      {children}
    </RefreshContext.Provider>
  );
};

export const useRefreshContext = () => {
  const context = useContext(RefreshContext);
  if (!context) {
    throw new Error('useRefreshContext must be used within RefreshProvider');
  }
  return context;
};
